import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowLeft, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { compareCards } from '@/lib/api';
import { useCompare } from '@/lib/hooks';
import { toast } from 'sonner';

const ROWS = [
  { key: 'bank_name', label: 'Bank' },
  { key: 'card_type', label: 'Type' },
  { key: 'annual_fee', label: 'Annual Fee', fmt: v => v === 0 ? 'FREE' : `AED ${v.toLocaleString()}`, highlight: true },
  { key: 'interest_rate_monthly', label: 'Interest Rate', fmt: v => v > 0 ? `${v}%/mo` : 'No Interest' },
  { key: 'cashback_rate', label: 'Cashback', fmt: v => v > 0 ? `${v}%` : '\u2014', highlight: true },
  { key: 'min_salary', label: 'Min Salary', fmt: v => `AED ${v.toLocaleString()}` },
  { key: 'welcome_bonus', label: 'Welcome Bonus' },
  { key: 'rewards_description', label: 'Rewards' },
  { key: 'foreign_transaction_fee', label: 'FX Fee', fmt: v => `${v}%` },
  { key: 'lounge_access', label: 'Lounge Access', fmt: v => v || 'None' },
  { key: 'insurance_coverage', label: 'Insurance', fmt: v => v || 'None' },
  { key: 'contactless', label: 'Contactless', fmt: v => v ? 'Yes' : 'No' },
  { key: 'is_islamic', label: 'Islamic', fmt: v => v ? 'Yes' : 'No' },
  { key: 'supplementary_cards', label: 'Supplementary', fmt: v => v > 0 ? `Up to ${v}` : 'None' },
];

export default function ComparePage() {
  const { compareIds, removeFromCompare, clearCompare } = useCompare();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (compareIds.length === 0) { setCards([]); setLoading(false); return; }
    setLoading(true);
    compareCards(compareIds).then(r => setCards(r.data)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, [compareIds]);

  if (compareIds.length === 0) return (
    <div className="max-w-4xl mx-auto px-6 py-20 text-center" data-testid="compare-empty">
      <h2 className="font-heading font-bold text-2xl text-[#09090B] mb-2">No Cards to Compare</h2>
      <p className="text-[#71717A] text-[15px] mb-6">Select up to 4 cards from the listing to compare side by side.</p>
      <Link to="/"><Button className="bg-[#002FA7] hover:bg-[#002175] text-white rounded-full h-10 px-6">Browse Cards</Button></Link>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="compare-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/" className="inline-flex items-center gap-2 text-[13px] text-[#71717A] hover:text-[#09090B] mb-2 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <h1 className="font-heading font-bold text-2xl text-[#09090B]">Compare Cards</h1>
        </div>
        <Button variant="outline" size="sm" data-testid="clear-compare-btn" onClick={clearCompare} className="text-[12px] rounded-full">
          Clear All
        </Button>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">{[...Array(10)].map((_, i) => <div key={i} className="h-12 bg-[#E4E4E7] rounded-lg" />)}</div>
      ) : (
        <div className="border border-[#E4E4E7] rounded-xl overflow-hidden">
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-[#E4E4E7]">
                  <th className="p-4 text-left text-[10px] font-bold uppercase tracking-wider text-[#71717A] w-40 bg-[#FAFAFA]">Attribute</th>
                  {cards.map(card => (
                    <th key={card.id} className="p-4 text-left border-l border-[#E4E4E7]">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="h-1 w-10 rounded-full mb-2" style={{ backgroundColor: card.bank_color }} />
                          <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: card.bank_color }}>{card.bank_name}</span>
                          <p className="font-heading font-bold text-[13px] text-[#09090B] mt-0.5">{card.card_name}</p>
                        </div>
                        <Button variant="ghost" size="sm" data-testid={`remove-compare-${card.id}`}
                          onClick={() => removeFromCompare(card.id)} className="h-6 w-6 p-0 text-[#A1A1AA] hover:text-red-500 rounded-full">
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row, i) => (
                  <tr key={row.key} className={`compare-row ${i < ROWS.length - 1 ? 'border-b border-[#F4F4F5]' : ''}`}>
                    <td className="p-4 text-[10px] font-bold uppercase tracking-wider text-[#71717A] bg-[#FAFAFA]">{row.label}</td>
                    {cards.map(card => {
                      const v = card[row.key];
                      const text = row.fmt ? row.fmt(v) : (v || '\u2014');
                      return (
                        <td key={card.id} className="p-4 border-l border-[#F4F4F5] text-[13px] text-[#09090B]">
                          <span className={row.highlight ? 'number-display font-semibold' : ''}>{text}</span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className="border-t border-[#E4E4E7]">
                  <td className="p-4 text-[10px] font-bold uppercase tracking-wider text-[#71717A] bg-[#FAFAFA] align-top">Features</td>
                  {cards.map(card => (
                    <td key={card.id} className="p-4 border-l border-[#F4F4F5] align-top">
                      <ul className="space-y-1.5">
                        {card.features.map((f, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-[12px] text-[#52525B]">
                            <Check className="w-3 h-3 text-[#059669] mt-0.5 shrink-0" />{f}
                          </li>
                        ))}
                      </ul>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex gap-3 mt-6">
        {cards.map(card => (
          <Link key={card.id} to={`/cards/${card.id}`}>
            <Button variant="outline" size="sm" className="text-[12px] rounded-full gap-1.5">View {card.card_name}</Button>
          </Link>
        ))}
      </div>
    </div>
  );
}
